import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

function parseJwtPayload(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return {};
  }
}

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('bestcars_token'));
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!token);
  const [role, setRole] = useState(() => {
    const t = localStorage.getItem('bestcars_token');
    return t ? (parseJwtPayload(t).role || 'admin') : null;
  });

  useEffect(() => {
    if (token) {
      localStorage.setItem('bestcars_token', token);
      setIsAuthenticated(true);
      const payload = parseJwtPayload(token);
      setRole(payload.role || 'admin');
      setUser({
        user_id: payload.user_id || payload.sub || '',
        email: payload.email || payload.sub || '',
        name: payload.name || '',
        picture: payload.picture || '',
        role: payload.role || 'admin',
      });
    } else {
      localStorage.removeItem('bestcars_token');
      setIsAuthenticated(false);
      setRole(null);
      setUser(null);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;

    const loadCurrentUser = async () => {
      try {
        const resp = await fetch(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (!resp.ok) throw new Error('Unable to load current user');
        const data = await resp.json();
        if (!cancelled) {
          setUser(data);
          setRole(data.role || 'admin');
        }
      } catch {
        if (!cancelled) setToken(null);
      }
    };

    loadCurrentUser();
    return () => { cancelled = true; };
  }, [token]);

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {}
    setToken(null);
    setUser(null);
    setRole(null);
  };

  const authAxios = axios.create({ baseURL: API });
  authAxios.interceptors.request.use(config => {
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
  authAxios.interceptors.response.use(r => r, err => {
    if (err.response?.status === 401) { setToken(null); }
    return Promise.reject(err);
  });

  const isAdmin = role === 'admin';

  return (
    <AuthContext.Provider value={{ token, setToken, isAuthenticated, user, setUser, logout, authAxios, API, role, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};
